import React from 'react';
import { motion } from 'framer-motion';
import {
  ResponsiveContainer, RadarChart, PolarGrid,
  PolarAngleAxis, Radar, Tooltip
} from 'recharts';
import {
  Shield, ArrowRight, Zap, BookOpen, ClipboardList,
  LayoutDashboard, AlertTriangle, PieChart, BarChart3, FolderLock
} from 'lucide-react';
import { Button } from '../ui/button';

const CategoryTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return <div className="rounded-lg border border-white/10 bg-slate-950/95 px-3 py-2 text-xs text-white shadow-xl">{payload[0].payload.fullLabel}: <strong>{payload[0].value}%</strong></div>;
};

const QuickActions = ({ stats, subscription, assessmentDetails, isAdminUser, navigate }) => {
  const categories = assessmentDetails ? [
    { label: 'Organization', fullLabel: 'Organization Basics', value: assessmentDetails.organization_score || 0 },
    { label: 'Core', fullLabel: 'Core Assessment', value: assessmentDetails.core_score || 0 },
    { label: 'Technical', fullLabel: 'Technical Controls', value: assessmentDetails.technical_score || 0 },
    { label: 'Docs', fullLabel: 'Documentation Readiness', value: assessmentDetails.documentation_score || 0 },
  ] : [];
  const showChart = categories.some((category) => category.value > 0);
  const assessmentsUsed = stats?.completed_assessments || 0;
  const assessmentsLimit = subscription?.assessments_limit || (subscription?.plan === 'pro' ? 15 : 5);
  const limitReached = assessmentsLimit < 999999 && assessmentsUsed >= assessmentsLimit;
  const scrollToUpgrade = () => document.getElementById('upgrade-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });

  return (
    <section className="mb-6 grid gap-6 lg:grid-cols-12" data-testid="dashboard-analysis-actions">
      <motion.div
        className="dashboard-glass rounded-xl p-5 sm:p-6 lg:col-span-7"
        initial={{ opacity: 0, x: -16 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.45, delay: 0.48 }}
        data-testid="category-performance-panel"
      >
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="dashboard-eyebrow">Readiness profile</p>
            <h2 className="mt-2 font-heading text-xl font-medium text-white">Category performance</h2>
          </div>
          <BarChart3 className="h-5 w-5 text-emerald-300" />
        </div>

        {showChart ? (
          <div className="mt-4 h-[300px] w-full" data-testid="category-performance-chart">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0} initialDimension={{ width: 560, height: 300 }}>
              <RadarChart data={categories} outerRadius="68%">
                <PolarGrid stroke="#334155" />
                <PolarAngleAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <Radar dataKey="value" stroke="#10b981" fill="#10b981" fillOpacity={0.24} strokeWidth={2} dot={{ r: 3, fill: '#f8fafc' }} />
                <Tooltip content={<CategoryTooltip />} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="mt-6 flex min-h-[270px] flex-col items-center justify-center rounded-lg border border-dashed border-white/10 bg-white/[0.025] px-5 text-center" data-testid="category-performance-empty-state">
            <PieChart className="mb-4 h-10 w-10 text-slate-600" />
            <p className="font-medium text-slate-300">Your readiness profile is taking shape</p>
            <p className="mt-2 max-w-sm text-sm leading-6 text-slate-500">Complete an assessment stage to compare maturity across organization, core, technical, and documentation categories.</p>
          </div>
        )}
      </motion.div>

      <motion.div
        className="dashboard-glass rounded-xl p-5 sm:p-6 lg:col-span-5"
        initial={{ opacity: 0, x: 16 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.45, delay: 0.55 }}
        data-testid="quick-actions-panel"
      >
        <div className="mb-6 flex items-center justify-between">
          <div><p className="dashboard-eyebrow">Workflow</p><h2 className="mt-2 font-heading text-xl font-medium text-white">Quick actions</h2></div>
          <Zap className="h-5 w-5 text-amber-300" />
        </div>
        <div className="space-y-3" data-tour="quick-actions" data-testid="quick-actions-list">
          {limitReached ? (
            <>
              <Button disabled className="h-12 w-full justify-between bg-slate-800 text-slate-500" data-testid="new-assessment-button-disabled">
                <span className="flex items-center"><Shield className="mr-3 h-4 w-4" />Start new assessment</span><AlertTriangle className="h-4 w-4 text-red-400" />
              </Button>
              <button type="button" onClick={scrollToUpgrade} className="w-full rounded-lg border border-amber-400/25 bg-amber-400/10 px-4 py-3 text-left text-sm text-amber-100 transition-colors hover:bg-amber-400/15" data-testid="assessment-limit-upgrade-button">Capacity reached — request more assessments</button>
            </>
          ) : (
            <Button onClick={() => navigate('/organization?new=true')} className="group h-12 w-full justify-between bg-emerald-500 text-slate-950 hover:bg-emerald-400" data-testid="new-assessment-button">
              <span className="flex items-center font-semibold"><Shield className="mr-3 h-4 w-4" />Start new assessment</span><ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          )}
          {assessmentDetails?.latest_org_id && (
            <Button onClick={() => navigate(`/assessment-summary/${assessmentDetails.latest_org_id}`)} variant="outline" className="group h-12 w-full justify-between border-white/10 bg-white/5 text-slate-200 hover:bg-white/10 hover:text-white" data-testid="view-latest-report-button">
              <span className="flex items-center"><PieChart className="mr-3 h-4 w-4 text-sky-300" />View latest report</span><ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          )}
          {subscription?.plan === 'pro' && (
            <Button onClick={() => navigate('/isms-document-control')} variant="outline" className="group h-12 w-full justify-between border-emerald-400/20 bg-emerald-400/5 text-slate-200 hover:bg-emerald-400/10 hover:text-white" data-testid="isms-document-control-button">
              <span className="flex items-center"><FolderLock className="mr-3 h-4 w-4 text-emerald-300" />ISMS Document Control</span><ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          )}
          {isAdminUser && (
            <>
              <Button onClick={() => navigate('/admin')} variant="outline" className="group h-12 w-full justify-between border-white/10 bg-white/5 text-slate-200 hover:bg-white/10 hover:text-white" data-testid="admin-panel-button">
                <span className="flex items-center"><LayoutDashboard className="mr-3 h-4 w-4 text-emerald-300" />Admin panel</span><ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button onClick={() => navigate('/audit-logs')} variant="outline" className="group h-12 w-full justify-between border-white/10 bg-white/5 text-slate-200 hover:bg-white/10 hover:text-white" data-testid="audit-log-button" data-tour="audit-logs">
                <span className="flex items-center"><ClipboardList className="mr-3 h-4 w-4 text-amber-300" />Audit logs</span><ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </>
          )}
          <Button onClick={() => navigate('/knowledge-base')} variant="outline" className="group h-12 w-full justify-between border-white/10 bg-white/5 text-slate-200 hover:bg-white/10 hover:text-white" data-testid="knowledge-base-button" data-tour="knowledge-base">
            <span className="flex items-center"><BookOpen className="mr-3 h-4 w-4 text-sky-300" />ISO 27001 knowledge base</span><ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Button>
        </div>
      </motion.div>
    </section>
  );
};

export default QuickActions;